import { supabase } from '@/lib/supabase-client';
import { Notification, CreateNotificationInput } from '@/types/notification';

/**
 * Fetch notifications for the current user
 */
export const fetchNotifications = async (limit = 10): Promise<Notification[]> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  // Try to fetch from notifications table if it exists
  let { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false })
    .limit(limit);

  // If notifications table doesn't exist, generate notifications from activity
  if (error?.code === '42P01' || error?.message?.includes('relation "notifications" does not exist')) {
    // Fallback: Generate notifications from various sources
    return generateNotificationsFromActivity(user.id, limit);
  }

  if (error) {
    console.error('Error fetching notifications:', error);
    return [];
  }

  // Transform database notifications to match our interface
  const notifications: Notification[] = (data || []).map(dbNotification => {
    // Handle both JSONB and string content formats
    let parsedContent: any = dbNotification.content;

    // If content is a string, try to parse it as JSON
    if (typeof dbNotification.content === 'string') {
      try {
        parsedContent = JSON.parse(dbNotification.content);
      } catch (e) {
        // If parsing fails, treat the string as the message
        parsedContent = { message: dbNotification.content };
      }
    }

    return {
      id: dbNotification.id,
      user_id: dbNotification.user_id,
      type: dbNotification.type,
      title: parsedContent?.title || 'Notification',
      message: parsedContent?.message || parsedContent || 'You have a new notification',
      link: parsedContent?.link,
      read: dbNotification.is_read,
      created_at: dbNotification.created_at,
      metadata: {
        sender_id: dbNotification.sender_id,
        reference_id: dbNotification.reference_id,
        reference_type: dbNotification.reference_type
      }
    };
  });

  // Remove duplicates based on title and message content
  const uniqueNotifications = notifications.filter((notification, index, self) =>
    index === self.findIndex(n =>
      n.title === notification.title && n.message === notification.message
    )
  );

  return uniqueNotifications;
};

/**
 * Generate notifications from user activity (fallback when notifications table doesn't exist)
 */
const generateNotificationsFromActivity = async (userId: string, limit: number): Promise<Notification[]> => {
  const notifications: Notification[] = [];
  
  try {
    // Check for new comments on user's posts
    const { data: postComments } = await supabase
      .from('post_comments')
      .select(`
        id,
        content,
        created_at,
        user_id,
        post_id,
        profiles!user_id(full_name, display_name)
      `)
      .eq('post_id', userId) // This would need proper join logic
      .order('created_at', { ascending: false })
      .limit(3);

    if (postComments) {
      postComments.forEach(comment => {
        notifications.push({
          id: `comment-${comment.id}`,
          user_id: userId,
          type: 'post_comment',
          title: 'New comment on your post',
          message: `${comment.profiles?.display_name || 'Someone'} commented on your post`,
          link: `/community/posts/${comment.post_id}`,
          read: false,
          created_at: comment.created_at,
          metadata: {
            sender_id: comment.user_id,
            sender_name: comment.profiles?.display_name || comment.profiles?.full_name,
            post_id: comment.post_id
          }
        });
      });
    }

    // Check for new marketplace activity
    const { data: listings } = await supabase
      .from('marketplace_listings')
      .select('id, title, created_at')
      .neq('seller_id', userId)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(2);

    if (listings) {
      listings.forEach(listing => {
        notifications.push({
          id: `listing-${listing.id}`,
          user_id: userId,
          type: 'marketplace',
          title: 'New marketplace listing',
          message: `New listing available: ${listing.title}`,
          link: `/marketplace/listings/${listing.id}`,
          read: false,
          created_at: listing.created_at,
          metadata: {
            listing_id: listing.id
          }
        });
      });
    }

    // Check for trip updates
    const { data: trips } = await supabase
      .from('trips')
      .select('id, title, start_date')
      .gte('start_date', new Date().toISOString())
      .order('created_at', { ascending: false })
      .limit(2);

    if (trips) {
      trips.forEach(trip => {
        notifications.push({
          id: `trip-${trip.id}`,
          user_id: userId,
          type: 'trip',
          title: 'New trip available',
          message: `Check out the upcoming trip: ${trip.title}`,
          link: `/trips/${trip.id}`,
          read: false,
          created_at: new Date().toISOString(),
          metadata: {
            trip_id: trip.id
          }
        });
      });
    }

    // Add a system notification
    notifications.push({
      id: 'system-welcome',
      user_id: userId,
      type: 'system',
      title: 'Welcome to Unimog Community Hub',
      message: 'Check out the latest features and community updates',
      link: '/dashboard',
      read: false,
      created_at: new Date().toISOString()
    });

  } catch (error) {
    console.error('Error generating notifications:', error);
  }

  // Sort by date and limit
  return notifications
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, limit);
};

/**
 * Mark notification as read
 */
export const markNotificationAsRead = async (notificationId: string): Promise<boolean> => {
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('id', notificationId);

  if (error) {
    console.error('Error marking notification as read:', error);
    return false;
  }

  return true;
};

/**
 * Mark all notifications as read for current user
 */
export const markAllNotificationsAsRead = async (): Promise<boolean> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('user_id', user.id)
    .eq('is_read', false);

  if (error) {
    console.error('Error marking all notifications as read:', error);
    return false;
  }

  return true;
};

/**
 * Get unread notification count
 */
export const getUnreadNotificationCount = async (): Promise<number> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return 0;

  const { count, error } = await supabase
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', user.id)
    .eq('is_read', false);

  if (error) {
    // If notifications table doesn't exist, return 0
    if (error.code === '42P01' || error.message?.includes('relation "notifications" does not exist')) {
      return 0;
    }
    console.error('Error getting unread count:', error);
    return 0;
  }

  return count || 0;
};

/**
 * Create a new notification
 */
export const createNotification = async (input: CreateNotificationInput): Promise<Notification | null> => {
  const { data, error } = await supabase
    .from('notifications')
    .insert({
      user_id: input.user_id,
      type: input.type,
      content: {
        title: input.title,
        message: input.message,
        link: input.link
      },
      reference_id: input.metadata?.reference_id,
      reference_type: input.metadata?.reference_type,
      sender_id: input.metadata?.sender_id,
      is_read: false,
      created_at: new Date().toISOString()
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating notification:', error);
    return null;
  }

  // Transform the returned data to match our interface
  if (data) {
    return {
      id: data.id,
      user_id: data.user_id,
      type: data.type,
      title: data.content?.title || input.title,
      message: data.content?.message || input.message,
      link: data.content?.link || input.link,
      read: data.is_read,
      created_at: data.created_at,
      metadata: {
        sender_id: data.sender_id,
        reference_id: data.reference_id,
        reference_type: data.reference_type,
        ...input.metadata
      }
    };
  }

  return null;
};

/**
 * Delete a notification
 */
export const deleteNotification = async (notificationId: string): Promise<boolean> => {
  const { error } = await supabase
    .from('notifications')
    .delete()
    .eq('id', notificationId);

  if (error) {
    console.error('Error deleting notification:', error);
    return false;
  }

  return true;
};